import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../services/api'
import { useContentMetaStore } from '../stores/contentMetaStore'
import { useAuthStore } from '../stores/authStore'
import FeaturedImageUpload from './FeaturedImageUpload'
import CategorySelector from './CategorySelector'

const defaultProductMeta = {
  sku: '',
  type: 'simple',
  status: 'draft',
  is_featured: false,
  name: '',
  slug: '',
  short_description: '',
  description: '',
  price: '',
  compare_at_price: '',
  track_inventory: true,
  stock_quantity: 0,
  low_stock_threshold: 5,
  allow_backorders: false,
  featured_image: '',
  meta_title: '',
  meta_description: '',
  tax_class: 'standard',
  weight: '',
  categories: [],
  cross_sells: [],
  template_id: null,
}

const defaultTemplateMeta = {
  name: '',
  description: '',
  thumbnail: '',
  target_entity_type: 'product',
  is_active: true,
  sync_blocks: true,
  sort_order: 0,
}

const slugify = (text) =>
  String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

const centsToDisplay = (cents) => {
  if (cents === '' || cents == null) return ''
  return (Number(cents) / 100).toFixed(2)
}

const normalizeIdList = (values) =>
  Array.isArray(values)
    ? [
        ...new Set(
          values
            .map((id) => Number(id))
            .filter((id) => !Number.isNaN(id) && id > 0),
        ),
      ]
    : []

export default function ProductMetaTabs({ entity, entityType, isNew }) {
  const useStore = useContentMetaStore(entityType)
  const meta = useStore((state) => state.meta)
  const setMeta = useStore((state) => state.setMeta)
  const updateField = useStore((state) => state.updateField)
  const user = useAuthStore((state) => state.user)
  const [activeTab, setActiveTab] = useState('general')
  const [slugTouched, setSlugTouched] = useState(false)
  const [showTemplatePicker, setShowTemplatePicker] = useState(false)
  const [crossSellSearch, setCrossSellSearch] = useState('')

  const isProduct = entityType === 'product'
  const isTemplate = entityType === 'template'
  const ecommerceEnabled = !!user?.features?.ecommerce_enabled

  const { data: templatesData } = useQuery({
    queryKey: ['content-templates', 'product', 'picker'],
    queryFn: async () => {
      const res = await api.get('/content-templates', {
        params: {
          target_entity_type: 'product',
          is_active: true,
          per_page: 100,
        },
      })
      return res.data
    },
    enabled: isProduct,
  })

  const templates = Array.isArray(templatesData)
    ? templatesData
    : Array.isArray(templatesData?.data)
      ? templatesData.data
      : []

  const { data: categoriesData } = useQuery({
    queryKey: ['categories', 'product-meta-tabs'],
    queryFn: async () => {
      const res = await api.get('/categories?active=true')
      return res.data
    },
    enabled: isProduct,
  })

  const categories = Array.isArray(categoriesData?.data)
    ? categoriesData.data
    : Array.isArray(categoriesData)
      ? categoriesData
      : []

  const { data: crossSellProductsData, isFetching: crossSellsLoading } =
    useQuery({
      queryKey: ['products', 'cross-sells', crossSellSearch],
      queryFn: async () => {
        const res = await api.get('/ecommerce/products', {
          params: {
            per_page: 100,
            search: crossSellSearch || undefined,
          },
        })
        return res.data
      },
      enabled: isProduct && ecommerceEnabled,
    })

  const crossSellOptions = Array.isArray(crossSellProductsData?.data)
    ? crossSellProductsData.data
    : Array.isArray(crossSellProductsData)
      ? crossSellProductsData
      : []

  const selectedCategories = useMemo(() => {
    const selectedIds = normalizeIdList(meta.categories)
    return selectedIds
      .map((id) => categories.find((category) => Number(category.id) === id))
      .filter(Boolean)
  }, [meta.categories, categories])

  const currentProductId = Number(entity?.id)

  const selectedCrossSellIds = useMemo(
    () => normalizeIdList(meta.cross_sells),
    [meta.cross_sells],
  )

  const crossSellLookup = useMemo(() => {
    const map = new Map()
    const entityCrossSells = Array.isArray(entity?.cross_sells)
      ? entity.cross_sells
      : []

    for (const product of [...crossSellOptions, ...entityCrossSells]) {
      map.set(Number(product.id), product)
    }

    return map
  }, [crossSellOptions, entity?.cross_sells])

  const selectedCrossSells = useMemo(
    () =>
      selectedCrossSellIds.map((id) => crossSellLookup.get(id)).filter(Boolean),
    [selectedCrossSellIds, crossSellLookup],
  )

  const availableCrossSellOptions = useMemo(
    () =>
      crossSellOptions.filter((product) => {
        const productId = Number(product.id)
        if (Number.isNaN(productId) || productId <= 0) return false
        if (!Number.isNaN(currentProductId) && productId === currentProductId) {
          return false
        }
        return true
      }),
    [crossSellOptions, currentProductId],
  )

  useEffect(() => {
    if (!isProduct || !isNew) return
    setShowTemplatePicker(true)
  }, [isProduct, isNew])

  useEffect(() => {
    if (!isNew || !isProduct || meta.name !== undefined) return
    setMeta(defaultProductMeta)
  }, [isNew, isProduct, meta.name, setMeta])

  useEffect(() => {
    if (!isNew || !isTemplate || meta.target_entity_type !== undefined) return
    setMeta(defaultTemplateMeta)
  }, [isNew, isTemplate, meta.target_entity_type, setMeta])

  useEffect(() => {
    if (!entity || isNew) return
    if (isProduct) {
      setMeta({
        sku: entity.sku || '',
        type: entity.type || 'simple',
        status: entity.status || 'draft',
        is_featured: entity.is_featured || false,
        name: entity.name || '',
        slug: entity.slug || '',
        short_description: entity.short_description || '',
        description: entity.description || '',
        price: centsToDisplay(entity.price),
        compare_at_price: centsToDisplay(entity.compare_at_price),
        track_inventory: entity.track_inventory ?? true,
        stock_quantity: entity.stock_quantity ?? 0,
        low_stock_threshold: entity.low_stock_threshold ?? 5,
        allow_backorders: entity.allow_backorders || false,
        featured_image:
          entity.featured_image?.url || entity.featured_image || '',
        meta_title: entity.meta_title || '',
        meta_description: entity.meta_description || '',
        tax_class: entity.tax_class || 'standard',
        weight: entity.weight ?? '',
        categories: (entity.categories || []).map((c) => c.id),
        cross_sells: (entity.cross_sells || []).map((p) => p.id),
        template_id: null,
      })
      setSlugTouched(true)
      return
    }
    if (isTemplate) {
      setMeta({
        name: entity.name || '',
        description: entity.description || '',
        thumbnail: entity.thumbnail || '',
        target_entity_type: entity.target_entity_type || 'product',
        is_active: entity.is_active ?? true,
        sync_blocks: entity.sync_blocks ?? true,
        sort_order: entity.sort_order ?? 0,
      })
    }
  }, [entity, isNew, isProduct, isTemplate, setMeta])

  const onNameChange = (value) => {
    updateField('name', value)
    if (!slugTouched && isProduct) {
      updateField('slug', slugify(value))
    }
  }

  const tabButtons = useMemo(() => {
    const tabs = [
      { id: 'general', label: 'General' },
      { id: 'pricing_inventory', label: 'Pricing & Inventory' },
      { id: 'content_media', label: 'Content & Media' },
      { id: 'seo_shipping', label: 'SEO & Shipping' },
    ]

    if (isProduct && ecommerceEnabled) {
      tabs.push({ id: 'cross_sell', label: 'Cross Sell' })
    }

    return tabs
  }, [ecommerceEnabled, isProduct])

  useEffect(() => {
    if (!tabButtons.some((tab) => tab.id === activeTab)) {
      setActiveTab('general')
    }
  }, [activeTab, tabButtons])

  if (isTemplate) {
    return (
      <div className="border-b border-gray-200 bg-white">
        <div className="px-6 py-4 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              value={meta.name || ''}
              onChange={(e) => updateField('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target
            </label>
            <select
              value={meta.target_entity_type || 'product'}
              onChange={(e) =>
                updateField('target_entity_type', e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="product">Product</option>
              <option value="page">Page</option>
              <option value="post">Post</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={meta.description || ''}
              onChange={(e) => updateField('description', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Thumbnail URL
            </label>
            <input
              value={meta.thumbnail || ''}
              onChange={(e) => updateField('thumbnail', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="border-b border-gray-200 bg-white">
      {showTemplatePicker && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Choose starting point
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Start from blank or use a template. You can still change this
              before first save.
            </p>
            <div className="space-y-2 max-h-64 overflow-auto mb-4">
              <button
                onClick={() => {
                  updateField('template_id', null)
                  setShowTemplatePicker(false)
                }}
                className={`w-full text-left px-3 py-2 border rounded-md ${
                  !meta.template_id
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-300'
                }`}
              >
                Blank product
              </button>
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => {
                    updateField('template_id', template.id)
                    setShowTemplatePicker(false)
                  }}
                  className={`w-full text-left px-3 py-2 border rounded-md ${
                    meta.template_id === template.id
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-300'
                  }`}
                >
                  <div className="font-medium text-sm text-gray-900">
                    {template.name}
                  </div>
                  {template.description && (
                    <div className="text-xs text-gray-600">
                      {template.description}
                    </div>
                  )}
                </button>
              ))}
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowTemplatePicker(false)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="px-6 pt-4">
        <div className="flex gap-2 border-b border-gray-200">
          {tabButtons.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2 text-sm border-b-2 ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-600'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-6 py-4">
        {activeTab === 'general' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                value={meta.name || ''}
                onChange={(e) => onNameChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Slug
              </label>
              <input
                value={meta.slug || ''}
                onChange={(e) => {
                  setSlugTouched(true)
                  updateField('slug', e.target.value)
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SKU
              </label>
              <input
                value={meta.sku || ''}
                onChange={(e) => updateField('sku', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={meta.type || 'simple'}
                  onChange={(e) => updateField('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="simple">Simple</option>
                  <option value="variable">Variable</option>
                  <option value="digital">Digital</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={meta.status || 'draft'}
                  onChange={(e) => updateField('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>
            {isNew && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template
                </label>
                <select
                  value={meta.template_id || ''}
                  onChange={(e) =>
                    updateField(
                      'template_id',
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Blank</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {activeTab === 'pricing_inventory' && (
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price (EUR)
              </label>
              <input
                type="number"
                value={meta.price || ''}
                onChange={(e) => updateField('price', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Compare Price
              </label>
              <input
                type="number"
                value={meta.compare_at_price || ''}
                onChange={(e) =>
                  updateField('compare_at_price', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Stock Quantity
              </label>
              <input
                type="number"
                value={meta.stock_quantity ?? 0}
                onChange={(e) => updateField('stock_quantity', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Low Stock Threshold
              </label>
              <input
                type="number"
                value={meta.low_stock_threshold ?? 5}
                onChange={(e) =>
                  updateField('low_stock_threshold', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={meta.track_inventory ?? true}
                  onChange={(e) =>
                    updateField('track_inventory', e.target.checked)
                  }
                />
                Track Inventory
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={meta.allow_backorders || false}
                  onChange={(e) =>
                    updateField('allow_backorders', e.target.checked)
                  }
                />
                Allow Backorders
              </label>
            </div>
          </div>
        )}

        {activeTab === 'content_media' && (
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Short Description
              </label>
              <textarea
                value={meta.short_description || ''}
                onChange={(e) =>
                  updateField('short_description', e.target.value)
                }
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={meta.description || ''}
                onChange={(e) => updateField('description', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Featured Image
              </label>
              <FeaturedImageUpload
                imageUrl={meta.featured_image || ''}
                onImageChange={(data) =>
                  updateField(
                    'featured_image',
                    typeof data === 'string' ? data : data?.url || '',
                  )
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categories
              </label>
              <CategorySelector
                selectedCategories={selectedCategories}
                onChange={(categoriesList) =>
                  updateField('categories', [
                    ...new Set(
                      (Array.isArray(categoriesList) ? categoriesList : [])
                        .map((category) => Number(category.id))
                        .filter((id) => !Number.isNaN(id) && id > 0),
                    ),
                  ])
                }
              />
            </div>
          </div>
        )}

        {activeTab === 'seo_shipping' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meta Title
              </label>
              <input
                value={meta.meta_title || ''}
                onChange={(e) => updateField('meta_title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tax Class
              </label>
              <input
                value={meta.tax_class || 'standard'}
                onChange={(e) => updateField('tax_class', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meta Description
              </label>
              <textarea
                value={meta.meta_description || ''}
                onChange={(e) =>
                  updateField('meta_description', e.target.value)
                }
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Weight (kg)
              </label>
              <input
                type="number"
                value={meta.weight || ''}
                onChange={(e) => updateField('weight', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>
        )}

        {activeTab === 'cross_sell' && ecommerceEnabled && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search products
              </label>
              <input
                value={crossSellSearch}
                onChange={(e) => setCrossSellSearch(e.target.value)}
                placeholder="Type product name or SKU"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div className="border border-gray-200 rounded-md max-h-64 overflow-y-auto">
              {crossSellsLoading ? (
                <div className="p-3 text-sm text-gray-500">
                  Loading products...
                </div>
              ) : availableCrossSellOptions.length === 0 ? (
                <div className="p-3 text-sm text-gray-500">
                  No products found
                </div>
              ) : (
                availableCrossSellOptions.map((product) => {
                  const productId = Number(product.id)
                  const selected = selectedCrossSellIds.includes(productId)
                  return (
                    <label
                      key={product.id}
                      className="flex items-center justify-between gap-3 px-3 py-2 border-b border-gray-100 last:border-b-0 cursor-pointer"
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-gray-900 truncate">
                          {product.name || `Product #${product.id}`}
                        </div>
                        <div className="text-xs text-gray-500">
                          {product.sku || '-'}{' '}
                          {product.status ? `- ${product.status}` : ''}
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateField('cross_sells', [
                              ...selectedCrossSellIds,
                              productId,
                            ])
                          } else {
                            updateField(
                              'cross_sells',
                              selectedCrossSellIds.filter(
                                (id) => id !== productId,
                              ),
                            )
                          }
                        }}
                      />
                    </label>
                  )
                })
              )}
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">
                Selected cross-sell products
              </div>
              {selectedCrossSells.length === 0 ? (
                <div className="text-sm text-gray-500">
                  No products selected
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {selectedCrossSells.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() =>
                        updateField(
                          'cross_sells',
                          selectedCrossSellIds.filter(
                            (id) => id !== Number(product.id),
                          ),
                        )
                      }
                      className="px-3 py-1 text-sm border border-gray-300 rounded-full hover:bg-gray-50"
                    >
                      {product.name || `Product #${product.id}`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
